import { WorkerResponse, statKey } from "@models/websockets";
import { unparse } from "papaparse";
import { format, formatISO, isSameDay } from "date-fns";
import { set } from 'idb-keyval';

import { BaseExtractionDatasource, ExportDataKey, replaceStringValuesForExport, statKeySort } from "./base.datasource";

export enum EDEFrequency {
  "yearly" = "yearly",
  "monthly" = "monthly",
  "weekly" = "weekly",
  "daily" = "daily",
  "UNKNOWN" = "UNKNOWN"
}

export function getEDEFrequency(periodicity: Duration): EDEFrequency {
  if(periodicity.years && periodicity.years == 1) {
    return EDEFrequency.yearly;
  } else if(periodicity.months && periodicity.months == 1) {
    return EDEFrequency.monthly;
  } else if (periodicity.days && periodicity.days == 7) {
    return EDEFrequency.weekly;
  } else if(periodicity.days && periodicity.days == 1) {
    return EDEFrequency.daily;
  }

  return EDEFrequency.UNKNOWN;
}

export interface TableConfiguration {
  feature_stats: statKey[];
  layer_name: string;
  date_format: string;
  frequency: EDEFrequency;
  unit: string;
  formula: Function;
  no_data: number;
}

export interface ExportEde {
  id: string;
  time: string;
  topic: string;
  type: string;
  indicator: string;
  value: string;
  frequency: string;
  unit: string;
}

export class TimeseriesExtractionDatasource extends BaseExtractionDatasource<TableConfiguration> {
  override createStringFromKeyObject(keyObject: ExportDataKey) {
    const date = new Date(parseInt(keyObject.layer_name));

    return replaceStringValuesForExport(`${this.tableConfiguration!.layer_name}::${format(date, this.tableConfiguration!.date_format)}::${keyObject.stat}`);
    // return replaceStringValuesForExport(`${this._tableConfiguration!.layer_name}::${format(keyObject.layer_time, this._tableConfiguration!.date_format)}::${keyObject.stat}`);
  }

  // private _attemptAddKeyEntry(existingHeaders: ExportDataKey[], keyEntry: ExportDataKey) {
  //   if (existingHeaders.some(dataHeader => this._createStringFromKeyObject(dataHeader) === this._createStringFromKeyObject(keyEntry))) { return };

  //   existingHeaders.push(keyEntry);
  // }


  override getTransformedResultFromResponse(workerResult: WorkerResponse, layer_id: string) {
    if (workerResult.result == undefined || this.tableConfiguration!.no_data === workerResult.result) {
      return 'no_data';
    } else {
      return this.tableConfiguration!.formula((workerResult.result) as Number).toFixed(2);
    }
  }


  public async downloadExport() {
    const propertyHeaders: string[] = this.propertyHeaders;
    const dataHeaders: ExportDataKey[] = this.headers.map(header => {
      const [time, stat] = header.split("::");
      return {
        layer_name: time,
        stat: stat as statKey
      }
    });

    for (let layer of this.layers) {
      const timeHeader: ExportDataKey = {
        layer_name: layer.layer_id,
        stat: 'time'
      };

      dataHeaders.push(timeHeader);
    }

    const dataArray: { [key: string]: string }[] = [];

    // TODO: Using promise with timeout here really is suboptimal, can we offload to webworker maybe?
    for (let i = 0; i < this.rows.length; i++) {
      const dataRow = this.rows[i];

      dataRow['unit'] = this.tableConfiguration?.unit!;

      for (let layer of this.layers) {
        const timeHeader: ExportDataKey = {
          layer_name: layer.layer_id,
          stat: 'time'
        };

        // dataHeaders.push(timeHeader);

        // const layerDate = new Date();
        // layerDate.setTime(parseInt(layer.layer_id));
        dataRow[this.createStringFromKeyObject(timeHeader)] = format(parseInt(layer.layer_id), this.tableConfiguration?.date_format!);// (Math.random() * 100).toString(); // TODO: Date formatting
      }


      // const promise = new Promise((resolve, reject) => {
      //   setTimeout(() => {
      //     // const feature = this.features[i];
      //     // const featureStats = this.getFeatureStats(feature.feature);

      //     // const featureData: { [key: string]: any } = {};

      //     // featureData['unit'] = this.tableConfiguration?.unit;

      //     // for (let layer of this.layers) {
      //     //   const layerTimeString = format(new Date(layer.layer_id), this.tableConfiguration!.date_format);

      //     //   const timeHeader: ExportDataKey = {
      //     //     layer_time: layer.layer_time,
      //     //     stat: 'time'
      //     //   };

      //     //   this._attemptAddKeyEntry(dataHeaders, timeHeader);
      //     //   featureData[this._createStringFromKeyObject(timeHeader)] = layerTimeString;// (Math.random() * 100).toString(); // TODO: Date formatting

      //     //   for (let stat of featureStats) {
      //     //     const statHeader: ExportDataKey = {
      //     //       layer_time: layer.layer_time,
      //     //       stat: stat
      //     //     };

      //     //     this._attemptAddKeyEntry(dataHeaders, statHeader);
      //     //     featureData[this._createStringFromKeyObject(statHeader)] = dataRow[`${layer.layer_id}::${stat}`];
      //     //   }
      //     // }

      //     // dataArray.push({
      //     //   ...feature.properties,
      //     //   ...featureData
      //     // });

      //     resolve(undefined);
      //   }, 0);
      // });

      const transformed: { [key: string]: string } = {};

      Object.entries(dataRow).forEach((entry: [string, string]) => {
        if (!entry[0].includes("::")) {
          transformed[entry[0]] = entry[1];
          return;
        }

        const [time, stat] = entry[0].split("::");
        const statHeader: ExportDataKey = {
          layer_name: time,
          stat: stat as statKey
        };

        transformed[this.createStringFromKeyObject(statHeader)] = entry[1];
      });

      // await promise;
      dataArray.push(transformed);
    }
    // layer_name: this.tableConfiguration!.layers[layer.layer_id].name,

    dataHeaders.sort((columnA, columnB) => {
      if (isSameDay(parseInt(columnA.layer_name), parseInt(columnB.layer_name))) {
        return statKeySort[columnA.stat] - statKeySort[columnB.stat];
      } else {
        return columnA.layer_name.localeCompare(columnB.layer_name);
      };
    });

    const csvResult = unparse(
      dataArray,
      {
        columns: [...propertyHeaders, 'unit', ...(dataHeaders.map(keyHeader => this.createStringFromKeyObject(keyHeader)))],
        quotes: true
      });

    await this.startCsvDownload(csvResult, `MOOD_export_${this.tableConfiguration?.layer_name}_${format(new Date(), 'yyyyMMdd_HHmm')}.csv`);

    // const data: Blob = new Blob([csvResult], {
    //   type: "text/csv;charset=utf-8"
    // });

    // const filename = ;

    // saveAs(data, filename);
  }

  public async edeExport() {
    const columns = [
      "id",
      "time",
      "topic",
      "type",
      "indicator",
      "value",
      "frequency",
      "unit"
    ];

    const dataArray: ExportEde[] = [];

    for(let row of this.rows) {
      for (let header of this.headers) {
        const [time, stat] = header.split("::");

        dataArray.push({
          id: row["id"],
          time: formatISO(parseInt(time), { representation: 'date' }),
          topic: this.tableConfiguration!.layer_name,
          type: 'numerical',
          indicator: stat,
          value: row[header],
          frequency: this.tableConfiguration!.frequency,
          unit: this.tableConfiguration!.unit
        })
      }
    }

    const csvResult = unparse({
      fields: columns, //[...filteredPropertyColumns.map(column => `${propertyColumnPrefix}${column}`), ...dataHeaders],
      data: dataArray // rows
    });


    const csvResultTetBytes = new TextEncoder().encode(csvResult);
    const binString = Array.from(csvResultTetBytes, (byte) =>
      String.fromCodePoint(byte),
    ).join("");
    const base64Data = `data:text/csv;charset=utf-8;base64,${btoa(binString)}`;

    await set('ede_data', base64Data)
  }

}
